import type {
  OverallDistribution,
  ScoreDistributionResponse,
  SubjectDistribution,
} from "@/client"
import { QuizzesService } from "@/client"
import { DistributionChart } from "@/components/Charts/DistributionChart"
import { useOrgColorScheme } from "@/hooks/useOrgColorScheme"
import { hexToRgba } from "@/utils/percentileColors"
import {
  Alert,
  Card,
  Flex,
  HStack,
  Heading,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"

interface ScoreDistributionCardProps {
  quizId: string
  attemptId: string
  showPercentileNumber?: boolean
}

type ColorFn = (p: number) => string

function ordinalSuffix(n: number): string {
  const abs = Math.abs(n)
  const mod10 = abs % 10
  const mod100 = abs % 100
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`
  if (mod10 === 1) return `${n}st`
  if (mod10 === 2) return `${n}nd`
  if (mod10 === 3) return `${n}rd`
  return `${n}th`
}

function SubjectMiniCard({
  subject,
  getColor,
  getTextColor,
  showPercentileNumber,
}: {
  subject: SubjectDistribution
  getColor: ColorFn
  getTextColor: ColorFn
  showPercentileNumber: boolean
}) {
  const pct =
    subject.student_percentile != null
      ? Math.round(subject.student_percentile)
      : null
  return (
    <Card.Root p={4} variant="outline">
      <Stack gap={2}>
        <HStack justify="space-between" align="center">
          <Text fontWeight="semibold" fontSize="sm">
            {subject.subject_name}
          </Text>
          {showPercentileNumber && pct != null && (
            <Text
              fontSize="xs"
              fontWeight="bold"
              style={{ color: getTextColor(pct) }}
            >
              {ordinalSuffix(pct)} pct
            </Text>
          )}
        </HStack>
        <DistributionChart
          bins={subject.bins}
          studentScore={subject.student_score}
          maxScore={subject.max_score}
          height={100}
          studentBarColor={pct != null ? getColor(pct) : undefined}
        />
      </Stack>
    </Card.Root>
  )
}

function PercentileRankPill({
  label,
  percentile,
  getColor,
  getTextColor,
  testId,
}: {
  label?: string
  percentile: number | null | undefined
  getColor: ColorFn
  getTextColor: ColorFn
  testId: string
}) {
  const pct = percentile != null ? Math.round(percentile) : null
  const fillColor = pct != null ? getColor(pct) : null
  const textColor = pct != null ? getTextColor(pct) : null

  return (
    <Text
      as="span"
      data-testid={testId}
      fontSize="sm"
      px={3}
      py={1}
      borderWidth="1px"
      borderRadius="full"
      whiteSpace="nowrap"
      color={textColor ?? "fg.muted"}
      // Band colors are tuned for light surfaces; lift them toward white in
      // dark mode so dark bands (e.g. green-1) stay legible.
      _dark={
        textColor
          ? { color: `color-mix(in srgb, ${textColor} 60%, white)` }
          : undefined
      }
      style={
        fillColor
          ? {
              borderColor: hexToRgba(fillColor, 0.35),
              background: hexToRgba(fillColor, 0.08),
            }
          : undefined
      }
    >
      {label && `${label} · `}
      <Text as="span" fontWeight="bold">
        {pct != null ? `${ordinalSuffix(pct)} percentile` : "not yet available"}
      </Text>
    </Text>
  )
}

function OverallCard({
  overall,
  getColor,
  getTextColor,
  showPercentileNumber,
}: {
  overall: OverallDistribution
  getColor: ColorFn
  getTextColor: ColorFn
  showPercentileNumber: boolean
}) {
  const pct =
    overall.student_percentile != null
      ? Math.round(overall.student_percentile)
      : null
  const fillColor = pct != null ? getColor(pct) : null
  const showRetakeRanks = overall.allows_retake === true
  const hasAnyRank =
    overall.student_percentile != null ||
    (showRetakeRanks && overall.first_attempt_percentile != null)

  return (
    <Card.Root p={6} variant="outline">
      <Stack gap={3}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
          <Heading size="sm">Score distribution</Heading>
          {showPercentileNumber && hasAnyRank && showRetakeRanks && (
            <Flex wrap="wrap" gap={2}>
              <PercentileRankPill
                label="1st attempt"
                percentile={overall.first_attempt_percentile}
                getColor={getColor}
                getTextColor={getTextColor}
                testId="percentile-rank-first"
              />
              <PercentileRankPill
                label="All attempts"
                percentile={overall.student_percentile}
                getColor={getColor}
                getTextColor={getTextColor}
                testId="percentile-rank-all"
              />
            </Flex>
          )}
          {showPercentileNumber && hasAnyRank && !showRetakeRanks && (
            <PercentileRankPill
              percentile={overall.student_percentile}
              getColor={getColor}
              getTextColor={getTextColor}
              testId="percentile-rank"
            />
          )}
        </Flex>
        <DistributionChart
          bins={overall.bins}
          studentScore={overall.student_score}
          maxScore={overall.max_score}
          height={180}
          studentBarColor={fillColor ?? undefined}
        />
        <Text fontSize="xs" color="fg.muted" textAlign="center">
          Your bar highlighted
        </Text>
        {showPercentileNumber && hasAnyRank && showRetakeRanks && (
          <Text fontSize="xs" color="fg.muted" textAlign="center">
            1st attempt ranks your first try against every student's first try.
            All attempts ranks this attempt against every attempt, retakes
            included.
          </Text>
        )}
      </Stack>
    </Card.Root>
  )
}

export function ScoreDistributionCard({
  quizId,
  attemptId,
  showPercentileNumber = true,
}: ScoreDistributionCardProps) {
  const { getColor, getTextColor } = useOrgColorScheme()
  const { data, isLoading, error } = useQuery<ScoreDistributionResponse>({
    queryKey: ["score-distribution", quizId, attemptId],
    queryFn: () =>
      QuizzesService.getScoreDistribution({
        quizId,
        attemptId,
      }),
  })

  if (isLoading) {
    return (
      <Stack gap={4}>
        <Skeleton height="240px" borderRadius="md" />
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
          <Skeleton height="140px" borderRadius="md" />
          <Skeleton height="140px" borderRadius="md" />
        </SimpleGrid>
      </Stack>
    )
  }

  if (error || !data) {
    return (
      <Alert.Root status="error">
        <Alert.Indicator />
        <Alert.Content>
          <Alert.Title>Could not load score distribution</Alert.Title>
        </Alert.Content>
      </Alert.Root>
    )
  }

  if (!data.is_ready) {
    return (
      <Alert.Root status="info">
        <Alert.Indicator />
        <Alert.Content>
          <Alert.Title>Percentile not yet available</Alert.Title>
          <Alert.Description>
            Percentile rankings will be available once at least 100 students
            have completed this quiz.
          </Alert.Description>
        </Alert.Content>
      </Alert.Root>
    )
  }

  return (
    <Stack gap={4}>
      {data.overall && (
        <OverallCard
          overall={data.overall}
          getColor={getColor}
          getTextColor={getTextColor}
          showPercentileNumber={showPercentileNumber}
        />
      )}
      {data.subjects.length > 1 && (
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
          {data.subjects.map((subject) => (
            <SubjectMiniCard
              key={subject.subject_id}
              subject={subject}
              getColor={getColor}
              getTextColor={getTextColor}
              showPercentileNumber={showPercentileNumber}
            />
          ))}
        </SimpleGrid>
      )}
    </Stack>
  )
}
